import { ChangeDetectorRef, Component, OnInit } from '@angular/core';

import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { EmployerService } from '../../../deduction-lists/services/employer.service';
import { ApplicationForm } from '../models/application-form.type';
import { Router, RouterModule } from '@angular/router';
// import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'not-employer',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './not-employer.component.html',
  styleUrls: ['./not-employer.component.scss'],
})
export class NotEmployerComponent implements OnInit {
  applicationForm!: FormGroup;
  showThirdPartyInput = false;
  submitting = false;
  selfProfileLoaded = false;
  selfOrganizationNumber: string | null = null;
  selfEmailConfirmed = false;
  showApplicationSubmittedModal = false;
  submitError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private employerService: EmployerService,
    private router: Router,
    private cdr: ChangeDetectorRef
    // public authService: AuthService
  ) {}

  ngOnInit() {
    this.applicationForm = this.fb.group({
      organizationNumber: ['', [Validators.pattern(/^\d{9}$/)]],
      organizationName: ['', [Validators.required]],
      organizations: this.fb.array([]),
      isThirdParty: [false],
    });

    void this.loadSelfProfile();

    this.applicationForm.controls['organizationNumber'].valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((value) => /^\d{9}$/.test(value || ''))
      )
      .subscribe(async (orgNumber) => {
        await this.tryAddOrganization(orgNumber!);
      });

    this.applicationForm.controls['isThirdParty'].valueChanges.subscribe((value) => {
      this.showThirdPartyInput = value;
    });
  }

  get organizations() {
    return this.applicationForm.get('organizations') as FormArray;
  }

  async tryAddOrganization(orgNumber: string) {
    // check duplicate
    if (this.organizations.controls.some((ctrl) => ctrl.value.organisasjonsnummer === orgNumber)) {
      this.applicationForm.controls['organizationNumber'].setErrors({ duplicate: true });
      return;
    }

    try {
      const org = await this.employerService.getOrganizationFromBrReg(orgNumber);
      this.applicationForm.controls['organizationName'].setValue(org.navn);

      this.organizations.push(
        this.fb.group({
          organisasjonsnummer: [org.organisasjonsnummer],
          navn: [org.navn],
        })
      );
      // reset inputfield
      this.applicationForm.controls['organizationNumber'].reset();
    } catch (err) {
      this.applicationForm.controls['organizationNumber'].setErrors({ notInBrReg: true });
    }
  }

  removeOrganization(index: number) {
    this.organizations.removeAt(index);
  }

  get requiresOwnOrganizationNumber(): boolean {
    return !!this.applicationForm?.controls['isThirdParty']?.value;
  }

  get hasSelfOrganizationNumber(): boolean {
    return !!String(this.selfOrganizationNumber ?? '').trim();
  }

  get hasConfirmedEmail(): boolean {
    return this.selfEmailConfirmed;
  }

  get hasRequiredProfileData(): boolean {
    return this.hasSelfOrganizationNumber && this.hasConfirmedEmail;
  }

  get isSubmitDisabled(): boolean {
    return this.applicationForm.invalid || this.submitting || this.organizations.length === 0 || !this.selfProfileLoaded || !this.hasRequiredProfileData;
  }

  async submit() {
    if (this.isSubmitDisabled) {
      this.applicationForm.markAllAsTouched();
      return;
    }

    this.submitting = true;
    this.submitError = null;
    try {
      const formValue = this.applicationForm.getRawValue();
      const payload: ApplicationForm[] = (formValue.organizations ?? []).map((org: { organisasjonsnummer: string; navn: string }) => ({
        organizationNumber: String(org?.organisasjonsnummer ?? '').trim(),
        organizationName: String(org?.navn ?? '').trim(),
      }));
      await this.employerService.applyForEmployer(payload);
      this.showApplicationSubmittedModal = true;
    } catch {
      this.submitError = 'Kunne ikke sende søknad akkurat nå. Prøv igjen.';
    } finally {
      this.submitting = false;
      this.cdr.detectChanges();
    }
  }

  async closeApplicationSubmittedModal(): Promise<void> {
    this.showApplicationSubmittedModal = false;
    await this.router.navigate(['/user-profile']);
  }

  private async loadSelfProfile(): Promise<void> {
    try {
      const profile = (await this.employerService.getSelf()) as Record<string, unknown>;

      const organizationNumber = String(profile?.['organizationNumber'] ?? profile?.['OrganizationNumber'] ?? '').trim();
      this.selfOrganizationNumber = organizationNumber || null;

      const emailAddress = String(profile?.['emailAddress'] ?? profile?.['EmailAddress'] ?? '').trim().toLowerCase();
      const confirmedEmailAddress = String(profile?.['confirmedEmailAddress'] ?? profile?.['ConfirmedEmailAddress'] ?? '')
        .trim()
        .toLowerCase();
      const hasMatchingConfirmedEmail = !!emailAddress && !!confirmedEmailAddress && confirmedEmailAddress === emailAddress;

      // Requirement is that the currently registered email is the confirmed one.
      this.selfEmailConfirmed = hasMatchingConfirmedEmail;
    } catch {
      this.selfOrganizationNumber = null;
      this.selfEmailConfirmed = false;
    } finally {
      this.selfProfileLoaded = true;
      this.cdr.detectChanges();
    }
  }
}
